import * as React from 'react';
import styled from 'styled-components';
import { SurveyTxt } from '../../SurveyTxt';
import { desktop } from '../../designSystem/BreakPoints';
import { P } from '../../designSystem/typography/Body';
import { RadioButtonListField } from '../../designSystem/formField/RadioButtonListField';
import { Checkbox } from '../../designSystem/inputs/Checkbox';
import { TextArea } from '../../designSystem/inputs/TextArea';
import { Button } from '../../designSystem/button/Button';
import { H2, H3 } from '../../designSystem/typography/Heading';
import { Card } from '../../designSystem/Card';
import { SurveyAnswerOkStatus, LanguageCode, fieldValidation, getAllThemeQuestions } from '../../amorg-common';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { colors } from '../../designSystem/colors/color';

export type SetQuestionSelectionProps = {
    txt:SurveyTxt,
    selectedLanguage:LanguageCode,
    answerStatus:SurveyAnswerOkStatus,  
    questionIndex: number,
    onPreviousClicked: () => void,
    onSubmitSelection: (value: number, comment: string) => void
}

export function SetQuestionSelection(props: SetQuestionSelectionProps): JSX.Element {
    const [selection, setSelection] = React.useState('');
    const [enableCommentOverwrite, setEnableCommentOverwrite] = React.useState(false);
    const [comment, setComment] = React.useState('');

    React.useEffect(() => {
        setSelection('');
        setComment('');
        setEnableCommentOverwrite(false);
    }, [props.questionIndex])

    function handleValueChange(value: string) {
        setSelection(value);
    }

    async function handleClickPrevious() {
        props.onPreviousClicked();
    }

    async function handleSubmitSelection() {
        if (selection !== '') {
            props.onSubmitSelection(
                Number(selection),
                commentToSubmit
            );
        }
    }

    const values: { text: string, value: string }[] = [
        { text: props.txt.setQuestionSelection_value01, value: "1" },
        { text: props.txt.setQuestionSelection_value02, value: "2" },
        { text: props.txt.setQuestionSelection_value03, value: "3" },
        { text: props.txt.setQuestionSelection_value04, value: "4" },
        { text: props.txt.setQuestionSelection_value05, value: "5" }
    ];

    const allThemeQuestions = getAllThemeQuestions(props.answerStatus.survey.themes);
    const questionCount = allThemeQuestions.length;
    const themedQuestion = allThemeQuestions[props.questionIndex];
    
    const theme = props.answerStatus.survey.themes[themedQuestion.themeIndex];
    const themeText:string = props.selectedLanguage === props.answerStatus.defaultLanguage
        ? theme.name
        : theme.translations.find(t => t.languageCode === props.selectedLanguage).text;   
    const questionText:string = props.selectedLanguage === props.answerStatus.defaultLanguage
        ? themedQuestion.question.title
        : themedQuestion.question.translations.find(t => t.languageCode === props.selectedLanguage).text;   


    // If resubmission, comment field will only be submitted if checkbox has mark
    const commentToSubmit = props.answerStatus.isCompleted
        ? (enableCommentOverwrite ? comment : undefined)
        : comment;
    const canSubmit =
        selection !== '' && fieldValidation.surveyAnswer.comment(commentToSubmit)


    return (
        <>
        <Flex flexDirection="column" gap={24}>
            <H2>{props.txt.setQuestionSelection_header(props.questionIndex + 1, questionCount)}</H2>
                <Flex flexDirection="column" gap={16}>
                    <H3>
                        <div style={{wordBreak:'break-all'}}>
                            {themeText}
                        </div>
                    </H3>
                    {props.answerStatus.isCompleted &&
                        <P><i>{props.txt.setQuestionSelection_overwriteWarning}</i></P>
                    }
                    
                    <Question>
                        <div style={{overflowWrap: 'break-word', whiteSpace: 'pre-wrap'}}>
                            <P>{questionText}</P>
                        </div>
                    </Question>
                    
                    <Card>
                        <AnswerRating>
                            <RadioButtonListField flexDirection='row' justifyContent='space-between'
                                label=""
                                options={values}
                                selectedValue={selection}
                                groupName='question-response'
                                onChange={handleValueChange}
                            />
                        </AnswerRating>
                    </Card>

                    {themedQuestion.question.canComment &&
                        <QuestionComment>
                            <small>{props.txt.setQuestionSelection_commentSubHeader}</small>
                            {props.answerStatus.isCompleted &&
                                <div>
                                    <Checkbox
                                        text={props.txt.setQuestionSelection_commentOverwriteWarning}
                                        value="enableCommentOverwrite"
                                        checked={enableCommentOverwrite}
                                        onChange={(_: string, checked: boolean) => setEnableCommentOverwrite(checked)} />
                                </div>
                            }
                            
                            <TextArea
                                value={comment}
                                minRows={3}
                                onChange={(value) => setComment(value)}
                                disabled={props.answerStatus.isCompleted && !enableCommentOverwrite} />

                            {fieldValidation.surveyAnswer.comment(comment)
                                ? props.txt.setQuestionSelection_commentCharacterCount(comment.length)
                                : props.txt.setQuestionSelection_commentCharacterCountError(comment.length)
                            }
                        </QuestionComment>
                    }

                    <SurveyButtons>
                        
                        <Button variant="outlined"
                            onClick={handleClickPrevious}
                            text={props.txt.setQuestionSelection_buttonPreviousText} />
                        
                        <Button variant="filled"
                            disabled={!canSubmit}
                            onClick={handleSubmitSelection}
                            text={props.questionIndex + 1 === questionCount ? props.txt.setQuestionSelection_buttonFinishText : props.txt.setQuestionSelection_buttonNextText} />
                    </SurveyButtons>
                    
                
                    <AlignCenter>
                        <StepProgressBar currentStep={props.questionIndex + 1} totalSteps={questionCount} />
                    </AlignCenter>
            
                </Flex>
            
        </Flex>
        </>
    );
}

type StepProgressBarProps = {
    currentStep: number;
    totalSteps: number;
}
function StepProgressBar(props: StepProgressBarProps): JSX.Element {
    const [width, setWidth] = React.useState(0);
    const wrapperDivRef = React.useRef(null);

    React.useEffect(() => {
        setWidth(wrapperDivRef?.current?.clientWidth || 0);
    }, []);

    return (
        <div ref={wrapperDivRef} style={{ width: '100%' }}>
            {width > 0 &&
                <ProgressBarContainer>
                    <ProgressBarFill style={{ width: `${props.currentStep / props.totalSteps * 100}%` }} />
                </ProgressBarContainer>
            }
        </div>
    );
}

const AnswerRating = styled.div`
    ul {
        display: flex;
        flex-direction: column !important;
        justify-content: center;
        align-items: start;
        margin-bottom: 0;
        li {
            padding: 0px 40px;
        }
    }

    @media(min-width: ${desktop}px){
        ul {
            align-items: center;
            flex-direction: row !important;
            padding: 0px;
        }
    }
`

const AlignCenter = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;

    @media(min-width: ${desktop}px){
        flex-direction: row;
  }
`

const ProgressBarContainer = styled.div`
    background-color: ${colors.graphs.progress.pending};
`;
const ProgressBarFill = styled.div`
    background-color: ${colors.graphs.progressBar};
    height:20px;
`;

const QuestionComment = styled.div`

    & textarea{
        height: auto !important;
        padding: 5px;
    }
    
`

const Question = styled.div`
    white-space: pre-wrap;
`



const SurveyButtons = styled.div`
    display:flex;
    flex-direction: row;
    gap: 16px;
    justify-content: space-between;

    & Button{
        flex: 1;
    }

    @media(min-width: ${desktop}px){
        justify-content:end;

        & Button{
            flex: 0;
        }
  }
`